import { useEffect, useState } from "react";
import FloatingInput from "../fields/FloatingInput";
import FieldHint from "../fields/FieldHint";
import styles from "../fields/FloatingInput.module.css";
import { lookupCommunes, type Commune } from "@/lib/lookupCommunes";

interface StepLocalisationProps {
  codePostal: string;
  ville: string;
  codeInsee: string;
  onChange: (name: string, value: string) => void;
  onCommuneSelect: (codeInsee: string, nom: string) => void;
  errors: Record<string, string>;
}

export default function StepLocalisation({
  codePostal,
  ville,
  codeInsee,
  onChange,
  onCommuneSelect,
  errors,
}: StepLocalisationProps) {
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const cp = codePostal.trim();
    if (!/^[0-9]{5}$/.test(cp)) {
      setCommunes([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    lookupCommunes(cp, "reseau", controller.signal)
      .then((list) => {
        setCommunes(list);
        // Auto-sélection si une seule commune pour ce CP
        // (évite un clic inutile pour les CP non ambigus).
        if (list.length === 1) {
          onCommuneSelect(list[0].code_insee, list[0].nom);
        } else if (list.length === 0) {
          onCommuneSelect("", "");
        } else if (codeInsee && !list.some((c) => c.code_insee === codeInsee)) {
          // Reset si la commune précédemment sélectionnée ne correspond plus
          // au nouveau CP (l'utilisateur a changé de CP après avoir choisi).
          onCommuneSelect("", "");
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codePostal]);

  const showDropdown = communes.length > 0;
  const noResult = !loading && codePostal.length === 5 && communes.length === 0;

  return (
    <fieldset style={{ border: "none" }}>
      <legend
        style={{
          fontSize: 16,
          fontWeight: 500,
          marginBottom: 12,
          display: "block",
          width: "100%",
        }}
      >
        Quelle est la localisation de votre projet ?
      </legend>
      {/* Placé au-dessus de la rangée (et non dans le champ) : un hint inline
          décalerait le code postal par rapport à la ville, alignés côte à côte. */}
      <FieldHint>
        Renseignez le code postal du lieu des travaux, et non celui de votre résidence principale si
        celui-ci diffère.
      </FieldHint>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 160px", minWidth: 140 }}>
          <FloatingInput
            name="codePostal"
            label="Code postal"
            value={codePostal}
            onChange={onChange}
            error={errors.codePostal}
            required
            maxLength={5}
          />
        </div>
        <div style={{ flex: "2 1 220px" }}>
          {showDropdown ? (
            <div className={styles.field}>
              <label className={styles.label} htmlFor="commune">
                Ville *
              </label>
              <select
                id="commune"
                name="commune"
                className={`${styles.input} ${errors.ville ? styles.hasError : ""}`}
                value={codeInsee}
                onChange={(e) => {
                  const selected = communes.find((c) => c.code_insee === e.target.value);
                  if (selected) onCommuneSelect(selected.code_insee, selected.nom);
                  else onCommuneSelect("", "");
                }}
              >
                <option value="">— Choisir —</option>
                {communes.map((c) => (
                  <option key={c.code_insee} value={c.code_insee}>
                    {c.nom}
                  </option>
                ))}
              </select>
              {errors.ville && <span className={styles.errorLabel}>{errors.ville}</span>}
            </div>
          ) : (
            <FloatingInput
              name="ville"
              label={loading ? "Chargement…" : "Ville"}
              value={ville}
              onChange={onChange}
              error={noResult ? "Aucune commune trouvée pour ce code postal" : errors.ville}
              required
            />
          )}
        </div>
      </div>
    </fieldset>
  );
}
